/* Os czasu dostawy Wolt w karcie zamowienia (tasks/wolt_update.md, A3). Dane z listy
   zamowien przez WebSocket: historia statusow z BE i zdarzenia z webhooka Wolta. */

export interface StatusEntry {
  status: string;
  timestamp: string;
}

export interface DeliveryEvent {
  type: string;
  at: string;
  courierVehicleType?: string | null;
}

export interface DeliveryInfo {
  statusHistory?: StatusEntry[] | null;
  deliveryEvents?: DeliveryEvent[] | null;
  woltTrackingUrl?: string | null;
  pickupEta?: string | null;
  dropoffEtaMin?: string | null;
  dropoffEtaMax?: string | null;
  woltRetryUntil?: string | null;
}

export interface TimelineEntry {
  // Klucz w orders.timeline.* (pl.json / en.json).
  labelKey: string;
  at: string;
  vehicle?: string | null;
}

// Tyle po pickup ETA kurier ma na odbior, potem BE anuluje zamowienie (DeliveryRules.PICKUP_GRACE).
export const PICKUP_GRACE_MINUTES = 30;

const STATUS_LABELS: Record<string, string> = {
  PAID: 'paid',
  IN_PREPARATION: 'confirmed',
  ON_THE_WAY: 'pickedUp',
  DELIVERED: 'delivered',
  CANCELLED: 'cancelled',
};

const EVENT_LABELS: Record<string, string> = {
  'order.received': 'woltAccepted',
  'order.rejected': 'woltRejected',
  'order.pickup_started': 'courierToRestaurant',
  'order.pickup_arrival': 'courierAtRestaurant',
  'order.picked_up': 'pickedUp',
  'order.dropoff_started': 'courierToCustomer',
  'order.dropoff_arrival': 'courierAtCustomer',
  'order.dropoff_completed': 'delivered',
  'order.delivered': 'delivered',
  'order.customer_no_show': 'customerNoShow',
};

// Ten sam moment z dwoch zrodel (status ON_THE_WAY i zdarzenie picked_up, delivered
// i dropoff_completed) pokazujemy raz - najwczesniejszy.
const MILESTONES = new Set(['pickedUp', 'delivered']);

const time = (iso: string): number => new Date(iso).getTime();

export const buildDeliveryTimeline = (info: DeliveryInfo): TimelineEntry[] => {
  const entries: TimelineEntry[] = [
    ...(info.statusHistory ?? [])
      .filter((entry) => STATUS_LABELS[entry.status] && entry.timestamp)
      .map((entry) => ({ labelKey: STATUS_LABELS[entry.status], at: entry.timestamp })),
    ...(info.deliveryEvents ?? [])
      .filter((event) => EVENT_LABELS[event.type] && event.at)
      .map((event) => ({
        labelKey: EVENT_LABELS[event.type],
        at: event.at,
        vehicle: event.courierVehicleType,
      })),
  ].sort((a, b) => time(a.at) - time(b.at));

  const seen = new Set<string>();

  return entries.filter((entry) => {
    if (!MILESTONES.has(entry.labelKey)) {
      return true;
    }
    if (seen.has(entry.labelKey)) {
      return false;
    }
    seen.add(entry.labelKey);
    return true;
  });
};

/** Termin odbioru, po ktorym BE anuluje zamowienie ze zwrotem; null bez pickup ETA. */
export const pickupDeadline = (pickupEta?: string | null): string | null =>
  pickupEta ? new Date(time(pickupEta) + PICKUP_GRACE_MINUTES * 60_000).toISOString() : null;

export const formatTime = (iso: string | null | undefined, locale: string): string =>
  iso
    ? new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(new Date(iso))
    : '—';
